import { FormEvent, useState } from 'react'
import './BulkTablesDialog.css'

interface Props {
  /** Where the tables will land, for the line that says so. */
  hallName?: string
  onCancel: () => void
  onConfirm: (count: number, template: string) => void
}

const MAX = 200

/**
 * Opening a room's worth of tables at once.
 *
 * A new restaurant does this on its first day, and one at a time it is twenty
 * prompts and twenty presses, the worst first impression the app can make.
 * Both answers are on one card rather than two dialogs in a row, so the
 * preview underneath can show what the pair produces before anything is
 * written.
 */
export function BulkTablesDialog({ hallName, onCancel, onConfirm }: Props) {
  const [count, setCount] = useState('20')
  const [pattern, setPattern] = useState('Masa #')

  const n = Number(count.trim())
  const countOk = Number.isInteger(n) && n >= 1 && n <= MAX

  // A pattern with no "#" is a name, and twenty tables sharing one name is
  // never what was meant, the number goes on the end instead of being
  // refused, because the intent is obvious.
  const template = pattern.includes('#') ? pattern.trim() : `${pattern.trim()} #`
  const nameFor = (i: number) => template.replace(/#/g, String(i))

  const ready = countOk && pattern.trim().length > 0

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!ready) return
    onConfirm(n, template)
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <form className="dlg__card" onSubmit={submit}>
        <h2>Toplu masa ekle</h2>
        {hallName && <p className="dlg__empty">Masalar “{hallName}” salonuna eklenir.</p>}

        <label className="dlgfield">
          <span>Kaç masa?</span>
          <input
            value={count}
            onChange={(e) => setCount(e.target.value.replace(/\D/g, '').slice(0, 3))}
            inputMode="numeric"
            placeholder="20"
          />
        </label>

        <label className="dlgfield">
          <span>Adlandırma</span>
          <input
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="Masa #"
          />
        </label>

        {/* Shown rather than explained: "# sıra numarasının yerine geçer" is a
            sentence someone has to hold in their head, and the first three
            names answer it outright. */}
        <div className="bulk__preview" aria-live="polite">
          <span className="bulk__label">Önizleme</span>
          {!countOk ? (
            <span className="bulk__warn">1 ile {MAX} arasında bir sayı yazın.</span>
          ) : (
            <span className="bulk__names">
              {nameFor(1)}, {nameFor(2)}, {nameFor(3)}
              {n > 4 && <> … {nameFor(n)}</>}
            </span>
          )}
        </div>

        <div className="dlg__buttons dlg__buttons--even">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Vazgeç
          </button>
          <button type="submit" className="btn btn--primary" disabled={!ready}>
            {countOk ? `${n} masa ekle` : 'Ekle'}
          </button>
        </div>
      </form>
    </div>
  )
}
